import isEmpty from "lodash/isEmpty";
import { CachedItem } from "@lib-shared/lib";
import { DecodedJwtTokens } from "core/auth/decoded-jwt-token";
import { JwtTokens } from "core/auth/jwt-tokens";
import { AllKeys, Key } from "core/constants";
import LocalStorageHelper from "core/lib/local-storage-helper";
import { CustomerAgreementWithPermissions, UserDetails } from "core/services/cef/permission.service.types";
import ConnectService from "core/services/connect.service";
import logApiError from "../logging/log-api-error";
import UrlUtils from "./url-utils";

const access = new CachedItem(Key.access, true, 0, false);
const campaignData = new CachedItem(Key.campaignData, true, 0, false);
const returnUrl = new CachedItem(Key.returnUrl, false, 0, false);
const loggedInUser = new CachedItem(Key.loggedInUser, true, 0, false);
const connectUser = new CachedItem(Key.connectUser, true, 0, false);

const cachedItems = [access, returnUrl, loggedInUser, connectUser];

export type SessionManagerEvents = {
    onSignIn: any;
    onSuccessfulAuth: (sessionManager: SessionManager) => void;
    onExpiredToken: (sessionManager: SessionManager) => void;
    onTokenRefresh: (sessionManager: SessionManager) => void;
    onStatusChange: (sessionManager: SessionManager) => void;
    onAuthCodeVerificationError: (sessionManager: SessionManager, error: any) => void;
    //onStaleToken?
    onExistingValidToken: (sessionManager: SessionManager) => void;
    onTokenVerificationError: (sessionManager: SessionManager) => void;
    onSignOut: () => void;
}

enum SessionState {
    notSignedIn,
    signedIn
}

// const logApiError = (error: any) => {
//     if (!!error?.isAxiosError) {
//         const axiosError = error as AxiosError;
//         console.error(axiosError.response?.data);
//         return;
//     }

//     console.error(error);
// }

const connectService = new ConnectService();

interface UserHandler {
    (): void;
}

export class SessionEvents {
    static OnSetUser: UserHandler;
}

type AccessToken = {
    accessToken: string,
    refreshToken: string,
    idToken: string,
    expiresIn: number
}

type SessionUser = {
    companies: UserDetails[],
    businessName: string,
    customerNumber: number,
    agreementId: number
}

class SessionManager {
    state = SessionState.notSignedIn;
    private _decodedTokens: DecodedJwtTokens | undefined;
    private _events?: SessionManagerEvents;
    private _isRefreshingToken = false;

    get isRefreshingToken() {
        return this._isRefreshingToken;
    }

    setAccess(accessToken: any) {
        access.set(accessToken);
        this._decodedTokens = new DecodedJwtTokens(accessToken);
    }

    get jwtTokens() {
        return access.value as JwtTokens;
    }

    get accessToken() {
        return this.jwtTokens?.accessToken;
    }

    get countryCode() {
        return this._decodedTokens?.access.country;
    }

    get email() {
        return connectUser.value?.email;
    }

    private _clear() {
        cachedItems.forEach(x => x.clear());
        this._decodedTokens = undefined;
    }

    clear() {
        this._clear();
    }

    /* When instantiated, check for existing token. Validate if present with re-auth option */
    constructor(private _refreshTokenMinsBeforeExpiry = 5, events: SessionManagerEvents | undefined = undefined) {
        this._events = events;

        if (!access.isCached) {
            return;
        }

        this._decodedTokens = new DecodedJwtTokens(access.value);

        //Could also check to see if issued by same issuer as env
        if (!this._decodedTokens.isValid) {
            events?.onExpiredToken(this);
            return;
        }

        this.validateTokenAsync();
    }

    async hasAccessAsync(checkLoggedInUser = true) {
        console.log("SessionManager: Checking access");

        //1.Does access token exist
        if (!this._decodedTokens) {
            return false;
        }

        const loggedInUser = this.loggedInUser;

        //Make sure decodeIdToken is set.
        if (isEmpty(this._decodedTokens?.id) || (checkLoggedInUser && isEmpty(loggedInUser))) {
            console.log("No access details found");
            return false;
        }

        //2. Check if token has expired
        const jwtToken = this._decodedTokens.access;

        if (jwtToken.isExpired) {
            console.log("Customer token has expired");

            LocalStorageHelper.logout();

            return false;
        }

        if (jwtToken && !this._isRefreshingToken) {
            this._isRefreshingToken = true;

            try {
                await this.refreshTokenIfCloseToExpirationAsync();
            } catch (error) {
                logApiError(error);
                return false;
            }

            this._isRefreshingToken = false;
        }

        return true;
    }

    signIn(returnUrl: string, language = "en") {
        this._clear();

        this.returnUrl = returnUrl;

        const url = connectService.buildSignInUrl(language);
        window.location.replace(url);
    }

    signUp(returnUrl: string, language = "en") {
        this._clear();

        this.returnUrl = returnUrl;

        const url = connectService.buildSignUpUrl(language);
        window.location.replace(url);
    }

    signOut(returnUrl: string) {
        this._clear();

        this._events?.onSignOut();

        LocalStorageHelper.logout();

        window.location.href = UrlUtils.parseUrl(returnUrl);
    }

    async getCustomerDetailsAsync() {
        if (!this.accessToken) {
            return null;
        }

        if (connectUser.isCached) {
            return connectUser.value;
        }

        try {
            const response = await connectService.getCustomerDetailsAsync(this.accessToken);

            connectUser.set(response.data);

            return connectUser.value;
        } catch (error) {
            logApiError(error);
        }
    }

    async validateTokenAsync() {
        try {
            const response = await connectService.validateAsync(this.jwtTokens, true);

            this._updateJwtTokens(response.data);

            this.state = SessionState.signedIn;

            return true;
        } catch (error) {
            logApiError(error);

            this._events?.onTokenVerificationError(this);

            return false;
        }
    }

    async verifyTokenAsync(authCode: string) {
        try {
            const response = await connectService.verifyAsync(authCode); //Direct

            this._updateJwtTokens(response.data);

            this.state = SessionState.signedIn;
        } catch (error) {
            logApiError(error);
            this._events?.onAuthCodeVerificationError(this, error);
        }
    }

    private _updateJwtTokens(tokens: JwtTokens) {
        const accessToken = this.jwtTokens?.accessToken;

        if (accessToken === tokens.accessToken) {
            return false;
        }

        access.set(tokens);
        this._decodedTokens = new DecodedJwtTokens(tokens);

        this._events?.onTokenRefresh(this);

        return true;
    }

    closeToExpiration() {
        const expiration = this._decodedTokens?.access.expiration;

        if (!expiration) {
            return true;
        }

        const date = new Date();

        const mins = date.getMinutes();

        date.setMinutes(mins + this._refreshTokenMinsBeforeExpiry);

        return date > expiration;
    }

    async refreshTokenIfCloseToExpirationAsync() {
        if (!this.closeToExpiration()) {
            return;
        }

        console.log("Token is close to expiration so refreshing");

        await this.refreshTokenAsync();
    }

    async refreshTokenAsync() {
        try {
            const response = await connectService.refreshTokenAsync(this.jwtTokens);

            this._updateJwtTokens(response.data);

            return true;
        } catch (error: any) {
            logApiError(error);

            return false;
        }
    }

    get loggedInUser() {
        return loggedInUser.value as SessionUser;
    }

    /**
    * @description Note this calls setLoggedInUser()
    */
    updateLoggedInUser(companies: UserDetails[]) {
        const user = this.loggedInUser;

        this.setLoggedInUser(companies, user?.customerNumber, user?.agreementId)
    }

    setLoggedInUser(companies: UserDetails[], customerNumber?: number, agreementId?: number) {
        if (!companies) {
            return;
        }

        let company = customerNumber != null
            ? companies.find((x: UserDetails) => x.customerNumber === customerNumber)
            : null;

        let agreement = company != null && agreementId != null
            ? company.agreements.find((x: CustomerAgreementWithPermissions) => x.id === agreementId)
            : null;

        company = company ?? companies[0];
        agreement = agreement ?? company.agreements[0];

        const user: SessionUser = {
            companies,
            businessName: company?.companyName,
            customerNumber: company?.customerNumber,
            agreementId: agreement?.id
        };

        loggedInUser.set(user);

        SessionEvents?.OnSetUser();
    }

    getUserDetailsForHeader() {
        const user = this.loggedInUser;
        return user == null ? null : `${user.customerNumber}:${user.agreementId}`;
    }

    getNameOfUser() {
        return this.getProfile();
    }

    getProfile() {
        const firstCompany = this.loggedInUser?.companies[0];

        return {
            firstName: firstCompany?.firstName,
            lastName: firstCompany?.lastName,
            countryCode: this.countryCode,
            email: this.email
        }
    }

    switchCompany(customerNumber: number, agreementId?: number) {
        const user = loggedInUser.value;

        console.log(`Switching company: ${customerNumber}`);

        this.setLoggedInUser(user?.companies, customerNumber, agreementId);

        const keysToKeep = [Key.loggedInUser, Key.access, Key.allBusinessDetails];

        for (let i = 0; i < AllKeys.length; i++) {
            const key = AllKeys[i];

            if (keysToKeep.includes(key)) {
                continue;
            }

            new CachedItem(key).clear();
        }
    }

    switchBranch(agreementId: number) {
        const user = loggedInUser.value;

        this.setLoggedInUser(user?.companies, user.customerNumber, agreementId);
    }

    get returnUrl() {
        return returnUrl.value;
    }

    set returnUrl(value: string) {
        returnUrl.set(value)
    }

    resetReturnUrl() {
        returnUrl.clear();
    }

    get campaignData() {
        return campaignData.value;
    }

    set campaignData(value: any) {
        campaignData.set(value);
    }
}

export default SessionManager;
